import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Types
interface Location {
  _id: string;
  name: string;
  address?: string;
  city?: string;
  country?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LocationState {
  locations: Location[];
  currentLocation: Location | null;
  loading: boolean;
  error: string | null;
}

interface LocationAction {
  type: string;
  payload?: any;
}

interface LocationContextType {
  locations: Location[];
  currentLocation: Location | null;
  loading: boolean;
  error: string | null;
  fetchLocations: () => Promise<void>;
  fetchLocation: (id: string) => Promise<Location | null>;
  createLocation: (locationData: Partial<Location>) => Promise<Location | null>;
  updateLocation: (id: string, locationData: Partial<Location>) => Promise<Location | null>;
  deleteLocation: (id: string) => Promise<boolean>;
  getLocationStatistics: (id: string, period?: string) => Promise<any>;
  setCurrentLocation: (location: Location) => void;
  clearError: () => void;
}

// Location Context
const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Location Reducer
const locationReducer = (state: LocationState, action: LocationAction): LocationState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LOCATIONS':
      return { ...state, locations: action.payload, loading: false };
    case 'SET_CURRENT_LOCATION':
      return { ...state, currentLocation: action.payload };
    case 'ADD_LOCATION':
      return { ...state, locations: [...state.locations, action.payload] };
    case 'UPDATE_LOCATION':
      return {
        ...state,
        locations: state.locations.map((loc: Location) =>
          loc._id === action.payload._id ? action.payload : loc
        )
      };
    case 'DELETE_LOCATION':
      return {
        ...state,
        locations: state.locations.filter((loc: Location) => loc._id !== action.payload)
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial State
const initialState = {
  locations: [],
  currentLocation: null,
  loading: false,
  error: null
};

// Location Provider
interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider = ({ children }: LocationProviderProps) => {
  const [state, dispatch] = useReducer(locationReducer, initialState);


  // Set loading
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Set error
  const setError = (error: string) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Fetch all locations
  const fetchLocations = async (filters = {}) => {
    try {
      setLoading(true);
      clearError();

      const params = new URLSearchParams(filters);
      
      const response = await api.get(`/locations?${params}`);

      if (response.data.success) {
        dispatch({ type: 'SET_LOCATIONS', payload: response.data.data });
      } else {
        setError(response.data.message || 'Lokasyonlar getirilemedi');
      }
    } catch (error) {
      console.error('Lokasyonlar getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Lokasyonlar getirilemedi');
    }
  };

  // Fetch single location
  const fetchLocation = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/locations/${id}`);

      if (response.data.success) {
        dispatch({ type: 'SET_CURRENT_LOCATION', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Lokasyon getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Lokasyon getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Lokasyon getirilemedi');
      return null;
    }
  };

  // Create location
  const createLocation = async (locationData: Partial<Location>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/locations`, locationData);

      if (response.data.success) {
        dispatch({ type: 'ADD_LOCATION', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Lokasyon oluşturulamadı');
        return null;
      }
    } catch (error) {
      console.error('Lokasyon oluşturma hatası:', error);
      setError((error as any).response?.data?.message || 'Lokasyon oluşturulamadı');
      return null;
    }
  };

  // Update location
  const updateLocation = async (id: string, locationData: Partial<Location>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.put(`/locations/${id}`, locationData);

      if (response.data.success) {
        dispatch({ type: 'UPDATE_LOCATION', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Lokasyon güncellenemedi');
        return null;
      }
    } catch (error) {
      console.error('Lokasyon güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'Lokasyon güncellenemedi');
      return null;
    }
  };

  // Delete location
  const deleteLocation = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.delete(`/locations/${id}`);

      if (response.data.success) {
        dispatch({ type: 'DELETE_LOCATION', payload: id });
        return true;
      } else {
        setError(response.data.message || 'Lokasyon silinemedi');
        return false;
      }
    } catch (error) {
      console.error('Lokasyon silme hatası:', error);
      setError((error as any).response?.data?.message || 'Lokasyon silinemedi');
      return false;
    }
  };

  // Get location statistics
  const getLocationStatistics = async (id: string, period: string = 'today') => {
    try {
      const response = await api.get(`/locations/${id}/statistics?period=${period}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'İstatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('İstatistik getirme hatası:', error);
      setError((error as any).response?.data?.message || 'İstatistikler getirilemedi');
      return null;
    }
  };

  // Set current location
  const setCurrentLocation = (location: Location) => {
    dispatch({ type: 'SET_CURRENT_LOCATION', payload: location });
  };

  // Context value
  const value = {
    ...state,
    fetchLocations,
    fetchLocation,
    createLocation,
    updateLocation,
    deleteLocation,
    getLocationStatistics,
    setCurrentLocation,
    setLoading,
    setError,
    clearError
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Location Hook
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
